import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {AbstractControl} from '@angular/forms';
import {windowResize$} from 'app/core/helpers/dom';
import {compareObjects, iterable} from 'app/core/helpers/object';
import {CountryPickerComponent} from 'app/shared/components/country-picker/country-picker.component';
import {DropDownListComponent} from 'app/shared/components/drop-down-list/drop-down-list.component';
import {DropDownPickerComponent} from 'app/shared/components/drop-down-picker/drop-down-picker.component';
import {InlineSelectorComponent} from 'app/shared/components/inline-selector/inline-selector.component';
import {PriceInputComponent} from 'app/shared/components/price-input/price-input.component';
import {BehaviorSubject, merge, Subscription} from 'rxjs';
import {debounceTime, startWith, switchMap, tap} from 'rxjs/operators';
import {InputErrorsComponent} from '../input-errors/input-errors.component';

type Constructor<T extends Object = Object> = new (...args: Array<any>) => T;

const INPUTS = new Map(
    iterable<any, Constructor>({
        input: HTMLInputElement,
        'price-input': PriceInputComponent,
        select: HTMLSelectElement,
        textarea: HTMLTextAreaElement,
        'drop-down-list': DropDownListComponent,
        'country-picker': CountryPickerComponent,
        'drop-down-picker': DropDownPickerComponent,
        'inline-selector': InlineSelectorComponent,
    })
);

// TODO: show when input on focus
@Component({
    selector: 'input-errors-tooltip',
    templateUrl: './input-errors-tooltip.component.html',
    styleUrls: ['../tooltip-outlet/tooltip-outlet.component.scss', './input-errors-tooltip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class InputErrorsTooltipComponent implements OnChanges, OnDestroy {
    @Input() control: AbstractControl;
    @ViewChild('tooltipBody', {static: false}) $tooltipEl: ElementRef<HTMLDivElement>;
    @ViewChild('errorsComponent', {static: false}) errorsComponent: InputErrorsComponent;
    @HostBinding('class.visible') visible: boolean;
    positionStyles$ = new BehaviorSubject({});

    private previousErrors: any;
    private inputEl: HTMLElement;
    private subscription = Subscription.EMPTY;

    public readonly debounceTime = 300;

    constructor(
        private $el: ElementRef<HTMLElement>,
        private cd: ChangeDetectorRef
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        this.subscription.unsubscribe();
        this.inputEl = this.findInput();
        if (this.control) {
            this.subscription = merge(this.control.valueChanges, this.control.statusChanges)
                .pipe(
                    tap(() => {
                        this.visible = compareObjects(this.control.errors, this.previousErrors, [/\.value$/]);
                        this.cd.detectChanges();
                    }),
                    switchMap(v => windowResize$.pipe(startWith(v))),
                    debounceTime(this.debounceTime)
                )
                .subscribe(() => {
                    this.visible = !!this.control.errors;
                    this.previousErrors = this.control.errors;
                    return this.positionStyles$.next(this.getPositionStyles());
                });
        }
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    getPositionStyles() {
        if (this.inputEl) {
            let rect = this.inputEl.getBoundingClientRect();
            let tooltipRect = this.$tooltipEl.nativeElement.getBoundingClientRect();
            return {
                '--x': `${rect.width / 2 + this.inputEl.offsetLeft}px`,
                '--y': `calc(var(--arrow-size-negative) + ${this.inputEl.offsetTop}px)`,
                '--dx': `-${tooltipRect.width / 2}px`,
                '--dy': `-${tooltipRect.height}px`,
            };
        }
        return {};
    }

    private findInput(): HTMLElement {
        let el = this.$el.nativeElement.previousElementSibling as HTMLElement;
        let constructors = [...INPUTS.values()];
        if (constructors.some(constructor => el instanceof constructor)) {
            return el;
        } else {
            let selector = [...INPUTS.keys()].join(', ');
            return el.matches(selector) ? el : el.querySelector(selector);
        }
    }
}
